import React, { useMemo } from 'react';
import { Line } from '@react-three/drei';
import { ThreeElements } from '@react-three/fiber';
import { createSelector } from 'reselect';
import { ColorRepresentation, DoubleSide, MeshBasicMaterial, Shape } from 'three';

import { BearingEnvelopeType } from '@paragon/contract-library';
import { LineSegment2D, Polygon2D } from '@paragon/geometry-library';

import { useAppSelector } from '../../../components/hooks';
import { convertFromLayoutToDesignCoordinates } from '../../../library/layout-geometry/coordinates';
import { getVisibleTrussEnvelopes } from '../../../store/selectors/layoutDataSelectors';
import { LAYOUT_MEMBER_EDGE_COLOR } from '../../component/MemberVisual';
import { EnvelopePlyDividers } from '../EnvelopePlyDividers';
import { getBearingEnvelopeColors, layoutColors } from '../layoutColors';
import { raycastLayoutCoordinatesPolygon } from '../raycastLayoutCoordinatesPolygon';
import { Segment } from '../Segments';
import { BearingHatching } from './BearingHatching';
import { HatchingMode } from './generateEnvelopeHatchLines';

export const FLAT_BEARING_Z_HEIGHT = 0.001; // minuscule offset to avoid z-fighting with grid lines
const EMPHASISED_ENVELOPE_OFFSET = 0.001; // another offset to ensure emphasised envelopes display above others
export const SOLID_BEARING_OPACITY = 0.8;

const getMinimumVisibleTrussEnvelopeElevation = createSelector(getVisibleTrussEnvelopes, (envelopes) =>
  Math.min(...envelopes.map((envelope) => envelope.elevation)),
);

const BEARING_SOLID_MATERIALS: Record<string, MeshBasicMaterial> = Object.fromEntries(
  [
    ...Object.values(layoutColors.bearingEnvelopes),
    ...Object.values(layoutColors.beamEnvelopes),
    ...Object.values(layoutColors.nonStructuralBearingEnvelopes),
  ].map((color) => [
    color,
    new MeshBasicMaterial({
      transparent: true,
      opacity: SOLID_BEARING_OPACITY,
      side: DoubleSide,
      color,
    }),
  ]),
);

interface BearingFlatVisualProps extends Partial<ThreeElements['group']> {
  bearingType: BearingEnvelopeType;
  shape: Polygon2D;
  structural: boolean;
  style: 'faded' | 'emphasised' | 'normal';
  topElevation: number;
  solidFill: boolean;
  hatchingMode?: HatchingMode;
  plyDividers?: readonly LineSegment2D[];
  dashed?: boolean;
}

export function BearingFlatVisual({
  bearingType,
  shape,
  structural,
  style,
  topElevation,
  solidFill,
  hatchingMode,
  plyDividers,
  dashed,
  ...rest
}: BearingFlatVisualProps) {
  const trussEnvelopeElevation = useAppSelector(getMinimumVisibleTrussEnvelopeElevation);
  const emphasisedOffset = style === 'emphasised' ? EMPHASISED_ENVELOPE_OFFSET : 0;
  const effectiveHeight = FLAT_BEARING_Z_HEIGHT + emphasisedOffset;

  // Truss envelopes can be smaller than bearing envelopes and (from a top down view) entirely contained within them.
  //   We want truss envelopes to be prioritized over truss envelopes at all times. This is a hamfisted approach, but
  //   hasn't caused problems so far.
  const raycastElevation = Math.min(topElevation, trussEnvelopeElevation - 1 / 16, 0) + emphasisedOffset;

  const userData = useMemo(() => ({ raycastPolygon: shape, raycastElevation }), [raycastElevation, shape]);

  const color = getBearingEnvelopeColors(bearingType, structural)[style];

  return (
    <group userData={userData} raycast={raycastLayoutCoordinatesPolygon} {...rest}>
      {solidFill && <SolidVisual shape={shape} color={color} effectiveHeight={effectiveHeight} />}

      <WireframeVisual
        shape={shape}
        color={solidFill ? LAYOUT_MEMBER_EDGE_COLOR : color}
        effectiveHeight={effectiveHeight + 1 / 16}
        dashed={dashed}
      />

      {hatchingMode && <BearingHatching shape={shape} mode={hatchingMode} color={color} />}

      {plyDividers && <EnvelopePlyDividers segments={plyDividers} height={effectiveHeight} color={color} />}
    </group>
  );
}

function SolidVisual(props: { shape: Polygon2D; color: string; effectiveHeight: number }) {
  const { vertices } = props.shape;
  const solidShape = useMemo(() => {
    if (vertices.length === 0) return null;

    const result = new Shape().moveTo(vertices[vertices.length - 1].x, vertices[vertices.length - 1].y);

    for (const vertex of vertices) {
      result.lineTo(vertex.x, vertex.y);
    }

    return result;
  }, [vertices]);

  if (!solidShape) return null;

  return (
    <mesh
      rotation-x={-Math.PI / 2}
      position-y={props.effectiveHeight}
      raycast={neverRaycast}
      material={BEARING_SOLID_MATERIALS[props.color]}
    >
      <shapeGeometry args={[solidShape]} />
    </mesh>
  );
}

function WireframeVisual(props: {
  shape: Polygon2D;
  color: ColorRepresentation;
  effectiveHeight: number;
  dashed?: boolean;
}) {
  return props.shape.edges.map((edge, i) => {
    const basePoint = convertFromLayoutToDesignCoordinates(edge.basePoint.to3D(props.effectiveHeight)).toArray();
    const endPoint = convertFromLayoutToDesignCoordinates(edge.endPoint.to3D(props.effectiveHeight)).toArray();

    if (props.dashed) {
      return (
        <Line
          key={i}
          dashed
          dashSize={3}
          gapSize={2}
          lineWidth={1}
          color={props.color}
          points={[basePoint, endPoint]}
        />
      );
    }

    return <Segment key={i} color={props.color} start={basePoint} end={endPoint} />;
  });
}

const neverRaycast = () => {};
